import { Injectable } from '@angular/core';
import {
    CompanyEmployeeCountDTO,
    CompanyManagementControllerService,
    EmployeeManagementControllerService as CompanyEmployeeService
} from '@iota/inh-core-v1-management-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, switchMap, take, withLatestFrom } from 'rxjs';
import {
    CompanyEmployeeActionFailure,
    CreateCompanyEmployee,
    CreateCompanyEmployeeSuccess,
    DeleteCompanyEmployee,
    DeleteCompanyEmployeeSuccess,
    LoadCompanyEmployeeDetailsByCompany,
    LoadCompanyEmployeeDetailsFailure,
    LoadCompanyEmployeeDetailsSuccess,
    LoadCompanyEmployeeOverviewPage,
    LoadCompanyEmployeeOverviewPageFailure,
    LoadCompanyEmployeeOverviewPageSuccess,
    LoadEmployeeFilterCounts,
    LoadEmployeeFilterCountsFailure,
    LoadEmployeeFilterCountsSuccess,
    UpdateCompanyEmployee,
    UpdateCompanyEmployeeSuccess
} from '../actions/company-employee.actions';
import { SMART_EMPLOYEE_OVERVIEW_FILTER } from 'src/app/shared/constants-and-enums/smart-employee-overview-types';
import { companyEmployeeLastOverviewPageParams } from '../reducer/company-employee.reducer';
import { ToastService } from '../../shared/services/toast.service';
import { toOverviewPageReload } from './employee-overview-reload';

@Injectable()
export class CompanyEmployeeEffects {
    loadCompanyEmployeesByCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadCompanyEmployeeDetailsByCompany),
            mergeMap(({ companyId }) =>
                this.companyEmployeeService.findAll(companyId, 0, 100_000).pipe(
                    take(1),
                    map((page) => LoadCompanyEmployeeDetailsSuccess({ employees: page.content, companyId })),
                    catchError((error) => of(LoadCompanyEmployeeDetailsFailure({ error })))
                )
            )
        )
    );

    loadCompanyEmployeeOverviewPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadCompanyEmployeeOverviewPage),
            mergeMap(({ params }) => {
                let onlyRegistered: boolean | undefined;
                let onlyNotRegistered: boolean | undefined;
                let onlyFormer: boolean | undefined;
                let onlyActive: boolean | undefined;

                switch (params.filter) {
                    case SMART_EMPLOYEE_OVERVIEW_FILTER.REGISTERED:
                        onlyActive = true;
                        onlyRegistered = true;
                        break;
                    case SMART_EMPLOYEE_OVERVIEW_FILTER.NOT_REGISTERED:
                        onlyActive = true;
                        onlyNotRegistered = true;
                        break;
                    case SMART_EMPLOYEE_OVERVIEW_FILTER.FORMER:
                        onlyFormer = true;
                        break;
                    case SMART_EMPLOYEE_OVERVIEW_FILTER.ALL:
                    default:
                        onlyActive = true;
                        break;
                }

                return this.companyEmployeeService
                    .findAll(
                        params.companyId,
                        params.pageIndex,
                        params.pageSize,
                        params.officeIds,
                        params.employeeGroups,
                        params.searchQuery,
                        onlyRegistered,
                        onlyNotRegistered,
                        onlyFormer,
                        onlyActive,
                        params.sortField,
                        params.sortAscending ?? true
                    )
                    .pipe(
                        take(1),
                        map((page) =>
                            LoadCompanyEmployeeOverviewPageSuccess({
                                employees: page.content,
                                totalElements: page.totalElements,
                                pageIndex: params.pageIndex,
                                pageSize: params.pageSize,
                                companyId: params.companyId
                            })
                        ),
                        catchError((error) => of(LoadCompanyEmployeeOverviewPageFailure({ error })))
                    );
            })
        )
    );

    loadEmployeeFilterCounts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadEmployeeFilterCounts),
            mergeMap(({ companyId }) => {
                return this.companyManagementService.getEmployeeCounts(companyId).pipe(
                    take(1),
                    map((counts: CompanyEmployeeCountDTO) =>
                        LoadEmployeeFilterCountsSuccess({
                            counts: {
                                all: counts.activeEmployeeCount ?? 0,
                                registered: counts.activeAndRegisteredEmployeeCount ?? 0,
                                notRegistered: counts.activeAndNotRegisteredEmployeeCount ?? 0,
                                former: counts.formerEmployeeCount ?? 0
                            }
                        })
                    ),
                    catchError((error) =>
                        of(
                            LoadEmployeeFilterCountsFailure({
                                error
                            })
                        )
                    )
                );
            })
        )
    );

    createCompanyEmployee$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CreateCompanyEmployee),
            withLatestFrom(this.store.select(companyEmployeeLastOverviewPageParams)),
            mergeMap(([{ request }, lastParams]) =>
                this.companyEmployeeService.createEmployee(request).pipe(
                    take(1),
                    switchMap((employee) => [
                        CreateCompanyEmployeeSuccess({ employee }),
                        LoadEmployeeFilterCounts({ companyId: request.companyId }),
                        ...toOverviewPageReload(request.companyId, lastParams)
                    ]),
                    catchError((error) => {
                        this.toastService.showError(
                            $localize`Sorry, there was a technical issue. The employee could not be created. If the problem persists please contact customer support.`
                        );
                        return of(CompanyEmployeeActionFailure({ error }));
                    })
                )
            )
        )
    );

    updateCompanyEmployee$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpdateCompanyEmployee),
            withLatestFrom(this.store.select(companyEmployeeLastOverviewPageParams)),
            mergeMap(([{ companyEmployeeId, request, companyId }, lastParams]) =>
                this.companyEmployeeService.updateEmployee(companyEmployeeId, request).pipe(
                    take(1),
                    switchMap((employee) => [
                        UpdateCompanyEmployeeSuccess({ employee }),
                        LoadEmployeeFilterCounts({ companyId }),
                        ...toOverviewPageReload(companyId, lastParams)
                    ]),
                    catchError((error) => {
                        this.toastService.showError(
                            $localize`Sorry, there was a technical issue. The employee could not be updated. If the problem persists please contact customer support.`
                        );
                        return of(CompanyEmployeeActionFailure({ error }));
                    })
                )
            )
        )
    );

    deleteCompanyEmployee$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeleteCompanyEmployee),
            withLatestFrom(this.store.select(companyEmployeeLastOverviewPageParams)),
            mergeMap(([{ companyEmployeeId, companyId }, lastParams]) =>
                this.companyEmployeeService.deleteEmployee(companyEmployeeId).pipe(
                    take(1),
                    switchMap(() => [
                        DeleteCompanyEmployeeSuccess({ companyEmployeeId, companyId }),
                        LoadEmployeeFilterCounts({ companyId }),
                        ...toOverviewPageReload(companyId, lastParams)
                    ]),
                    catchError((error) => {
                        this.toastService.showError(
                            $localize`Sorry, there was a technical issue. The employee could not be deleted. If the problem persists please contact customer support.`
                        );
                        return of(CompanyEmployeeActionFailure({ error }));
                    })
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private companyEmployeeService: CompanyEmployeeService,
        private companyManagementService: CompanyManagementControllerService,
        private store: Store,
        private toastService: ToastService
    ) {}
}
